import React, { useCallback, useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import AuthenticatedLayout from '@screens/Authenticated/Layout/Layout';
import { SharedStyles as Shared } from '@screens/Authenticated/Property/sharedStyles';
import { Styles as FormStyles } from '@screens/Authenticated/Property/NewRegisterScreen/style';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import { CircularProgress, Snackbar, Alert } from '@mui/material';
import ProtoHeader from '@components/Header/ProtoHeader';

// ─── Phone mask ──────────────────────────────────────────────────────────────
const maskPhone = (value: string): string => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10)
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

// ─── Form shape ──────────────────────────────────────────────────────────────
interface IFormData {
  nome_empresa: string;
  creci: string;
  endereco_empresa: string;
  whatsapp_empresa: string;
  email_empresa: string;
  smtp_email: string;
  header_background_uri: string;
  header_contact_info_position: string;
  footer_logo_uri: string;
  limite_imoveis_destaque: number;
}

const EMPTY: IFormData = {
  nome_empresa: '',
  creci: '',
  endereco_empresa: '',
  whatsapp_empresa: '',
  email_empresa: '',
  smtp_email: '',
  header_background_uri: '',
  header_contact_info_position: '30,140',
  footer_logo_uri: '',
  limite_imoveis_destaque: 5
};

// ─── Image picker field ───────────────────────────────────────────────────────
interface ImageFieldProps {
  value: string;
  uploading: boolean;
  onUpload: (file: File) => void;
  hint?: string;
}

const ImageField: React.FC<ImageFieldProps> = ({
  value,
  uploading,
  onUpload,
  hint
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div>
      {value ? (
        <div
          style={{
            marginBottom: 10,
            border: '1px solid #e0e0e0',
            borderRadius: 6,
            overflow: 'hidden',
            display: 'inline-flex',
            background: '#fafafa'
          }}>
          <img
            src={value}
            alt="preview"
            style={{
              maxWidth: 280,
              maxHeight: 110,
              objectFit: 'contain',
              display: 'block'
            }}
          />
        </div>
      ) : (
        <div
          style={{
            width: 280,
            height: 80,
            border: '1px dashed #ccc',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#bbb',
            fontSize: 13,
            marginBottom: 10,
            background: '#fafafa'
          }}>
          Sem imagem
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            e.target.value = '';
          }}
        />
        <FormStyles.Button
          type="button"
          disabled={uploading}
          style={{
            opacity: uploading ? 0.6 : 1,
            fontSize: 13,
            padding: '6px 16px'
          }}
          onClick={() => inputRef.current?.click()}>
          {uploading ? 'Enviando…' : value ? 'Alterar' : 'Carregar'}
        </FormStyles.Button>
        {uploading && <CircularProgress size={16} />}
      </div>
      {hint && (
        <span
          style={{
            fontSize: 11,
            color: '#aaa',
            marginTop: 4,
            display: 'block'
          }}>
          {hint}
        </span>
      )}
    </div>
  );
};

// ─── Screen ───────────────────────────────────────────────────────────────────
const ConfigCompanyDataScreen: React.FC = () => {
  const {
    configuracoes,
    fetchConfiguracoes,
    updateConfiguracoes,
    uploadConfiguracaoImagem
  } = useMetadataStore();

  const [formData, setFormData] = useState<IFormData>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingField, setUploadingField] = useState<keyof IFormData | null>(
    null
  );
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // ─── Header preview (html2canvas capture) ─────────────────────────────────
  const HEADER_W = 1200;
  const HEADER_H = 215;
  const hiddenHeaderRef = useRef<HTMLDivElement>(null);
  const [previewSrc, setPreviewSrc] = useState<string>('');
  const [capturing, setCapturing] = useState(false);

  // Local blob URL for the header background — avoids CORS issues in html2canvas
  const [headerBgBlobUrl, setHeaderBgBlobUrl] = useState('');

  // Capture the header preview with html2canvas
  useEffect(() => {
    // Wait until the blob URL is ready before capturing
    if (formData.header_background_uri && !headerBgBlobUrl) return;

    const timer = setTimeout(async () => {
      if (!hiddenHeaderRef.current) return;
      setCapturing(true);
      try {
        const canvas = await html2canvas(hiddenHeaderRef.current, {
          useCORS: true,
          width: HEADER_W,
          height: HEADER_H,
          scale: 1,
          logging: false
        });
        setPreviewSrc(canvas.toDataURL('image/jpeg', 0.9));
      } catch (err) {
        console.warn('Falha ao capturar o header:', err);
        // fail silently — preview unavailable
      } finally {
        setCapturing(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [
    headerBgBlobUrl,
    formData.header_background_uri,
    formData.header_contact_info_position
  ]);

  // Initial fetch
  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchConfiguracoes();
      setLoading(false);
    })();
  }, [fetchConfiguracoes]);

  // Sync store → form
  useEffect(() => {
    if (!configuracoes) return;
    setFormData({
      nome_empresa: configuracoes.nome_empresa ?? '',
      creci: configuracoes.creci ?? '',
      endereco_empresa: configuracoes.endereco_empresa ?? '',
      whatsapp_empresa: configuracoes.whatsapp_empresa ?? '',
      email_empresa: configuracoes.email_empresa ?? '',
      smtp_email: configuracoes.smtp_email ?? '',
      header_background_uri: configuracoes.header_background_uri ?? '',
      header_contact_info_position:
        configuracoes.header_contact_info_position ?? '30,140',
      footer_logo_uri: configuracoes.footer_logo_uri ?? '',
      limite_imoveis_destaque: configuracoes.limite_imoveis_destaque ?? 6
    });
    // Fetch the header background as a local blob for html2canvas rendering
    const bgUri = configuracoes.header_background_uri;
    if (bgUri) {
      fetch(bgUri)
        .then((r) => {
          if (!r.ok) throw new Error(`fetch ${r.status}`);
          return r.blob();
        })
        .then((blob) => {
          setHeaderBgBlobUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return URL.createObjectURL(blob);
          });
        })
        .catch(() => {
          setHeaderBgBlobUrl(bgUri);
        });
    } else {
      setHeaderBgBlobUrl('');
    }
  }, [configuracoes]);

  const setField = useCallback(
    (field: keyof IFormData, value: string | number) =>
      setFormData((prev) => ({ ...prev, [field]: value })),
    []
  );

  const handleImageUpload = async (field: keyof IFormData, file: File) => {
    // Create a local blob URL for immediate preview (avoids CORS in html2canvas)
    if (field === 'header_background_uri') {
      setHeaderBgBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
    }
    setUploadingField(field);
    try {
      const url = await uploadConfiguracaoImagem(file);
      if (!url) throw new Error('no url');
      setFormData((prev) => ({ ...prev, [field]: url }));
    } catch {
      setErrorMsg('Erro ao enviar imagem. Tente novamente.');
    } finally {
      setUploadingField(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await updateConfiguracoes(formData);
      if (!ok) throw new Error();
      await fetchConfiguracoes();
      setSuccessMsg('Configurações salvas com sucesso!');
    } catch {
      setErrorMsg('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Shared styles ─────────────────────────────────────────────────────────
  const inputStyle: React.CSSProperties = {
    border: '1px solid #d9d9d9',
    borderRadius: 4,
    padding: '8px 12px',
    fontSize: 14,
    outline: 'none',
    background: '#fff',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
    width: '100%'
  };
  const focus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) =>
    (e.currentTarget.style.borderColor = '#125dae');
  const blur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) =>
    (e.currentTarget.style.borderColor = '#d9d9d9');

  const sectionTitle: React.CSSProperties = {
    fontSize: 15,
    fontWeight: 600,
    color: '#125dae',
    margin: '28px 0 16px',
    paddingBottom: 6,
    borderBottom: '2px solid #e8f0fb'
  };

  const row: React.CSSProperties = {
    display: 'flex',
    gap: 20,
    flexWrap: 'wrap',
    marginBottom: 20
  };

  const field = (width?: string): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: width ? `0 0 ${width}` : '1 1 200px',
    minWidth: 0
  });

  const label: React.CSSProperties = {
    fontSize: 13,
    fontWeight: 600,
    color: '#555'
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <AuthenticatedLayout>
        <Shared.PageSection>
          <h1>Dados da Empresa</h1>
          <Shared.RectangleBar />
          <div
            style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <CircularProgress />
          </div>
        </Shared.PageSection>
      </AuthenticatedLayout>
    );
  }

  return (
    <AuthenticatedLayout>
      <Shared.PageSection>
        <h1>Dados da Empresa</h1>
        <Shared.RectangleBar />

        {/* ── DADOS GERAIS ─────────────────────────────────────────── */}
        <h2 style={sectionTitle}>Dados Gerais</h2>

        <div style={row}>
          <div style={field('100%')}>
            <label style={label}>Nome da Empresa</label>
            <input
              style={inputStyle}
              value={formData.nome_empresa}
              onChange={(e) => setField('nome_empresa', e.target.value)}
              placeholder="ex.: Viva Real Imóveis"
              onFocus={focus}
              onBlur={blur}
            />
          </div>
        </div>

        <div style={row}>
          <div style={field('180px')}>
            <label style={label}>CRECI</label>
            <input
              style={inputStyle}
              value={formData.creci}
              onChange={(e) => setField('creci', e.target.value)}
              placeholder="ex.: 12345-F"
              onFocus={focus}
              onBlur={blur}
            />
          </div>
          <div style={field()}>
            <label style={label}>Endereço da Empresa</label>
            <input
              style={inputStyle}
              value={formData.endereco_empresa}
              onChange={(e) => setField('endereco_empresa', e.target.value)}
              placeholder="ex.: Rua das Flores, 123 – Centro"
              onFocus={focus}
              onBlur={blur}
            />
          </div>
        </div>

        {/* ── CONTATO ──────────────────────────────────────────────── */}
        <h2 style={sectionTitle}>Contato</h2>

        <div style={row}>
          <div style={field('200px')}>
            <label style={label}>WhatsApp</label>
            <input
              style={inputStyle}
              type="tel"
              value={maskPhone(formData.whatsapp_empresa)}
              onChange={(e) =>
                setField('whatsapp_empresa', maskPhone(e.target.value))
              }
              placeholder="(41) 99999-9999"
              onFocus={focus}
              onBlur={blur}
            />
          </div>
          <div style={field()}>
            <label style={label}>E-mail da Empresa</label>
            <input
              style={inputStyle}
              type="email"
              value={formData.email_empresa}
              onChange={(e) => setField('email_empresa', e.target.value)}
              placeholder="contato@empresa.com.br"
              onFocus={focus}
              onBlur={blur}
            />
          </div>
          <div style={field()}>
            <label style={label}>E-mail SMTP (envio de e-mails)</label>
            <input
              style={inputStyle}
              type="email"
              value={formData.smtp_email}
              onChange={(e) => setField('smtp_email', e.target.value)}
              placeholder="noreply@empresa.com.br"
              onFocus={focus}
              onBlur={blur}
            />
          </div>
        </div>

        {/* ── HEADER ───────────────────────────────────────────────── */}
        <h2 style={sectionTitle}>Header</h2>

        <div style={{ ...row, flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={label}>Imagem de Fundo do Header</label>
            <ImageField
              value={formData.header_background_uri}
              uploading={uploadingField === 'header_background_uri'}
              onUpload={(f) => handleImageUpload('header_background_uri', f)}
              hint="Dimensões recomendadas: 1200 × 215 px"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={label}>
              Posição do bloco de contato (CSS bottom / right em px)
            </label>
            {/* Off-screen full-size render — captured by html2canvas */}
            <div
              aria-hidden="true"
              style={{
                position: 'fixed',
                top: -9999,
                left: -9999,
                width: HEADER_W,
                height: HEADER_H,
                overflow: 'hidden',
                pointerEvents: 'none'
              }}>
              <div
                ref={hiddenHeaderRef}
                style={{ width: HEADER_W, height: HEADER_H }}>
                <ProtoHeader
                  outline={true}
                  backgroundUri={
                    headerBgBlobUrl || formData.header_background_uri
                  }
                  whatsapp="(15) 98137-0057"
                  overlayBottom={
                    Number(
                      formData.header_contact_info_position.split(',')[0]
                    ) || 30
                  }
                  overlayRight={
                    Number(
                      formData.header_contact_info_position.split(',')[1]
                    ) || 140
                  }
                />
              </div>
            </div>
            {/* Preview image */}
            <div
              style={{
                position: 'relative',
                borderRadius: 6,
                border: '1px solid #e0e0e0',
                overflow: 'hidden',
                background: '#f5f5f5'
              }}>
              {previewSrc ? (
                <img
                  src={previewSrc}
                  alt="Prévia do header"
                  style={{ width: '100%', height: 'auto', display: 'block' }}
                />
              ) : (
                <div
                  style={{
                    width: '100%',
                    paddingBottom: `${((HEADER_H / HEADER_W) * 100).toFixed(4)}%`,
                    position: 'relative'
                  }}>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#bbb',
                      fontSize: 13
                    }}>
                    {capturing ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Gerando prévia…'
                    )}
                  </div>
                </div>
              )}
              {capturing && previewSrc && (
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(255,255,255,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                  <CircularProgress size={24} />
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {(['bottom', 'right'] as const).map((axis, i) => {
                const parts = formData.header_contact_info_position.split(',');
                const val = parts[i] ?? (i === 0 ? '30' : '140');
                return (
                  <div
                    key={axis}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4
                    }}>
                    <span style={{ fontSize: 12, color: '#888' }}>
                      {axis} (px)
                    </span>
                    <input
                      style={{ ...inputStyle, width: 90 }}
                      type="number"
                      min={0}
                      value={val}
                      onChange={(e) => {
                        const next = [...parts];
                        next[i] = e.target.value;
                        setField(
                          'header_contact_info_position',
                          `${next[0] ?? '30'},${next[1] ?? '140'}`
                        );
                      }}
                      onFocus={focus}
                      onBlur={blur}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ── FOOTER ───────────────────────────────────────────────── */}
        <h2 style={sectionTitle}>Footer</h2>

        <div style={row}>
          <div style={field('300px')}>
            <label style={label}>Logo do Footer</label>
            <ImageField
              value={formData.footer_logo_uri}
              uploading={uploadingField === 'footer_logo_uri'}
              onUpload={(f) => handleImageUpload('footer_logo_uri', f)}
            />
          </div>
        </div>

        {/* ── DESTAQUES ────────────────────────────────────────────── */}
        <h2 style={sectionTitle}>Destaques</h2>

        <div style={row}>
          <div style={field('180px')}>
            <label style={label}>Limite de Imóveis em Destaque</label>
            <input
              style={inputStyle}
              type="number"
              min={1}
              max={999}
              value={formData.limite_imoveis_destaque}
              onChange={(e) =>
                setField(
                  'limite_imoveis_destaque',
                  Math.max(1, parseInt(e.target.value) || 1)
                )
              }
              onFocus={focus}
              onBlur={blur}
            />
          </div>
        </div>

        {/* ── SALVAR ───────────────────────────────────────────────── */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 32
          }}>
          <FormStyles.Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            style={{ opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Salvando…' : 'SALVAR'}
          </FormStyles.Button>
        </div>
      </Shared.PageSection>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMsg}
        autoHideDuration={5000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" onClose={() => setErrorMsg('')}>
          {errorMsg}
        </Alert>
      </Snackbar>
    </AuthenticatedLayout>
  );
};

export default ConfigCompanyDataScreen;
